import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { RootState } from '../../../app/store';
import { ConnectedProps, connect } from 'react-redux';
import { useIntl } from 'react-intl';
import messages from './messages';
import { useHistory } from 'react-router-dom';
import Button from '../../../components/Button/Button.component';
import DialogWrap from '../../../components/DiaogWrap/DiaogWrap.component';
import {
  accentColorSecond,
  fontColorPrim,
  fontColorSecond,
  red,
  white,
} from '../../../assets/styledAssets/vars';
import { StyledTable, Wrap } from '../../../assets/styledAssets/grid';
import {
  DeleteIcon,
  PlusIcon,
  SearchIcon,
} from '../../../components/Icons/Icons.component';
import { GridColumn } from '@progress/kendo-react-grid';
import { StyledTD } from './styles';
import { fetchList } from '../Questionnaires.actions';
import {
  setList,
  setSearch,
  addQuestionnaire,
  setTotal,
} from '../Questionnaires.slice';
import debounce from 'lodash/debounce';
import { StyledFormField } from '../../../components/FormField/styles';
import { Input } from '@progress/kendo-react-inputs';
import AddQuestinarie from '../components/AddQuestinarie/AddQuestinarie.component';
import QuestionnarieService from '../../../service/Questionnarie.service';
import { Questionnaire } from '../questionnaires';

const connector = connect(
  (state: RootState) => ({
    profile: state.app.user,
    list: state.questionnaires.list,
    search: state.questionnaires.search,
    isFetching: state.questionnaires.isFetching,
    total: state.questionnaires.total,
    pageSize: state.questionnaires.pageSize,
  }),
  {
    fetchList,
    setSearch,
    setList,
    addQuestionnaire,
    setTotal,
  },
);

type PropsFromRedux = ConnectedProps<typeof connector>;
interface QListTypes {
  url?: string;
}

const QuestionnairesList: FunctionComponent<PropsFromRedux & QListTypes> = ({
  url,
  list,
  search,
  isFetching,
  total,
  pageSize,
  fetchList,
  setSearch,
  setList,
  addQuestionnaire,
  profile,
  setTotal,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const [skip, setSkip] = useState<number>(0);
  const [showQuestionnaireDetails, setShowQuestionnaireDetails] = useState(
    false,
  );

  useEffect(() => {
    fetchList(true);
    // eslint-disable-next-line
  }, []);

  const fetch = useMemo(
    () =>
      debounce(() => {
        fetchList(true);
      }, 200),
    // eslint-disable-next-line
    [],
  );

  const handleChangeInput = (e: any) => {
    setSearch(e.target.value);
    fetch();
  };

  const pageHandler = useCallback(
    (event: any) => {
      setSkip(event.page.skip);
      if (
        skip + pageSize + 3 >= list.length &&
        !isFetching &&
        total > list.length
      ) {
        fetchList(false);
      }
    },
    [isFetching, list.length, total, fetchList, setSkip, skip, pageSize],
  );

  const deleteQuestion = (id: string) => {
    QuestionnarieService.deleteQuestionnaire(id).then(() => {
      const newList = list.filter((f) => f.id !== id);
      setList(newList);
      setTotal(newList.length);
    });
  };

  const handleAddEditQuestion = useCallback(
    (question: Partial<Questionnaire>) => {
      question.personId = profile?.id;
      QuestionnarieService.createQuestionnaire(question as Questionnaire).then(
        (res) => {
          if (!list.find((q) => q.name === question.name)) {
            if (!(res.languages && res.languages.length)) {
              res.languages = [
                { id: question.languageId as string, name: '', code: '' },
              ];
            }
            addQuestionnaire([res]);
            setTotal(total + 1);
          }
          setShowQuestionnaireDetails(false);
        },
      );
    },
    // eslint-disable-next-line
    [total],
  );

/*  const navToSendQuestions = (id: string, languageId: string) => {
    history.push(`${url}/send/${id}/${languageId}`);
  };*/

  const navToEditQuestion = (id: string, languageId: string) => {
    history.push(`${url}/edit/${id}/${languageId}`);
  };

  return (
    <>
      <Wrap flexDir={'column'} width={'100%'}>
        <Wrap alignItems={'center'} width={'100%'} justifyCont={'space-between'}>
          <Wrap alignItems={['flex-start', 'flex-end']} flexDir={['column', 'row']} margin={['0 0 2rem 0', '0']}>
            <Wrap
              fs={'3.2rem'}
              fw={'bold'}
              color={'#000'}
              alignItems={'flex-end'}
              borders={[[{ side: 'right', color: 'transparent' }], [{side: 'right', color: accentColorSecond}]]}
              padding={['0', '0 3rem 0 0']}
              margin={['0 0 2rem 0', '0 3rem 0 0']}
            >
              {intl.formatMessage(messages.title)}
            </Wrap>
            <Wrap
              fs={'1.8rem'}
              color={fontColorSecond}
              alignItems={'flex-end'}
              padding={'0 0 .5rem 0'}
            >
              {total} {intl.formatMessage(messages.total)}
            </Wrap>
          </Wrap>
          <Wrap justifyCont={'flex-end'}>
            <Button primary onClick={() => setShowQuestionnaireDetails(true)}>
              <PlusIcon color={white} width={'1.4rem'} />
              {intl.formatMessage(messages.addQuestionnaire)}
            </Button>
          </Wrap>
        </Wrap>
        <Wrap alignItems={'center'} margin={['2rem 0', '3rem 0']} maxWidth={'34rem'} width={'100%'}>
          <StyledFormField>
            <Input value={search} onChange={handleChangeInput} />
            <SearchIcon color={fontColorPrim} width={'2rem'} />
          </StyledFormField>
          {/*          <Column lg={70}>
            <Wrap justifyCont={'flex-end'}>
              <Button icon>
                <SettingsIcon width={'2rem'} color={accentColorSecond} />
              </Button>
            </Wrap>
          </Column>*/}
        </Wrap>
      </Wrap>
      <Wrap
        grow={'1'}
        padding={'0 1.5rem'}
        overflow={'hidden'}
        width={'100%'}
        height={'100%'}
        hiddenXS
      >
        <StyledTable
          style={{ height: '100%' }}
          data={list.slice(skip, skip + pageSize)}
          onPageChange={pageHandler}
          onRowClick={(event) =>
            navToEditQuestion(event.dataItem.id, event.dataItem.languages[0].id)
          }
          scrollable={'virtual'}
          skip={skip}
          pageSize={pageSize}
          total={total}
          rowHeight={55}
        >
          <GridColumn field="name" title="Questionnaire" width={'150px'} />
          <GridColumn field="description" title="Description" />
          <GridColumn
            width={'120px'}
            cell={(props: any) => (
              <StyledTD>
                <Wrap alignItems={'center'} justifyCont={'flex-end'}>
                  {/*                  <Button
                    icon
                    onClick={() =>
                      navToSendQuestions(
                        props.dataItem.id,
                        props.dataItem.languages[0].id,
                      )
                    }
                    children={
                      <SomeIcon width={'2rem'} color={accentColorPrim} />
                    }
                  />*/}
                  {/*                  <Button
                    icon
                    children={
                      <EditIcon width={'2rem'} color={accentColorPrim} />
                    }
                  />*/}
                  <Button
                    onClick={() => deleteQuestion(props.dataItem.id)}
                    icon
                    children={<DeleteIcon width={'2rem'} color={red} />}
                  />
                </Wrap>
              </StyledTD>
            )}
          />
        </StyledTable>
      </Wrap>

      <Wrap hiddenSM overflow={'auto'}>
        {list.map((question : any, index)=>
          <Wrap onClick={() => navToEditQuestion(question.id, question.languages[0].id)} flexDir={'column'} flexWrap={'wrap'} margin={'0 0 2rem 0'} width={'100%'} padding={'3rem'} br={'1.2rem'} bgColor={white} key={`card-${index}`}>
            <Wrap width={'100%'} alignItems={'center'} justifyCont={'space-between'}>
              <Wrap margin={'0 0 .6rem 0'} fs={'2rem'} fw={'bold'}>{question.name}</Wrap>
              <Button
                onClick={() => deleteQuestion(question.id)}
                icon
                children={<DeleteIcon width={'2rem'} color={red} />}
              />
            </Wrap>
            {question.description &&
            <Wrap margin={'2rem 0 0 0'} width={'100%'}>
              <Wrap fs={'1.4rem'} color={fontColorSecond}>{question.description}</Wrap>
            </Wrap>}
          </Wrap>)}
      </Wrap>

      {showQuestionnaireDetails && (
        <DialogWrap
          title={intl.formatMessage(messages.addQuestionnaire)}
          children={
            <AddQuestinarie
              onCancel={() => setShowQuestionnaireDetails(false)}
              onSave={handleAddEditQuestion}
            />
          }
        />
      )}
    </>
  );
};

export default connector(QuestionnairesList);
